/**
 * The API client.
 *
 * <p>The access token lives in memory only — never localStorage, where any script on the page
 * could read it. The refresh token is an httpOnly cookie the browser sends on its own, so a
 * reload recovers the session through {@link refreshSession} without the token ever being
 * readable from JavaScript.
 */

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export interface Session {
  accessToken: string;
  expiresInSeconds: number;
  email: string;
  fullName: string;
  role: Role;
  merchantCode: string | null;
  merchantAccountType: MerchantAccountType | null;
  mustChangePassword: boolean;
}

export type Role = 'ADMIN' | 'OPS' | 'FINANCE' | 'SUPPORT' | 'VIEWER';
export type MerchantAccountType = 'GENERAL' | 'VENDOR' | 'COMPANY';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
  }
}

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function setUnauthenticatedHandler(handler: () => void) {
  onUnauthenticated = handler;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set for endpoints that legitimately return no JSON, such as a CSV download. */
  raw?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 401 && !path.startsWith('/api/auth/')) {
    // A 15-minute access token expires mid-session as a matter of course; try the refresh cookie
    // once before treating it as a sign-out.
    const renewed = await refreshSession();
    if (renewed) return request<T>(path, options);
    onUnauthenticated?.();
    throw new ApiError(401, 'unauthorized', 'Your session has ended. Please sign in again.');
  }

  if (options.raw) {
    if (!response.ok) throw await toError(response);
    return (await response.text()) as unknown as T;
  }

  if (!response.ok) throw await toError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function toError(response: Response): Promise<ApiError> {
  try {
    // Errors are RFC 7807 problems carrying a stable `code` the console can branch on.
    const problem = await response.json();
    return new ApiError(
      response.status,
      problem.code ?? 'error',
      problem.detail ?? problem.title ?? response.statusText,
      problem,
    );
  } catch {
    return new ApiError(response.status, 'error', response.statusText);
  }
}

export async function login(email: string, password: string): Promise<Session> {
  const session = await request<Session>('/api/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  accessToken = session.accessToken;
  return session;
}

export async function refreshSession(): Promise<Session | null> {
  try {
    const session = await request<Session>('/api/auth/refresh', { method: 'POST' });
    accessToken = session.accessToken;
    return session;
  } catch {
    accessToken = null;
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await request('/api/auth/logout', { method: 'POST' });
  } finally {
    accessToken = null;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  getRaw: (path: string) => request<string>(path, { raw: true }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
